import { deskMay, frontDeskMay, isFrontDesk, type MyBusiness, type OwnerNudgeKind, type Sport } from "@teamna/shared";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import {
  Button,
  CheckIcon,
  EmptyState,
  ErrorState,
  Field,
  InlineError,
  LockIcon,
  PageHeader,
  SkeletonList,
  showToast,
} from "../components/index.ts";
import { newIdempotencyKey } from "@teamna/web-client/api/client.ts";
import { useOwnerHome, useSendNudges } from "@teamna/web-client/hooks/use-families.ts";
import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import { useMe, useUpdateMe } from "@teamna/web-client/hooks/use-member.ts";
import { useActiveBusiness, useRecordPayment, useSellPackage } from "@teamna/web-client/hooks/use-owner.ts";
import { useSetupProgress } from "@teamna/web-client/hooks/use-setup.ts";
import { useShape } from "@teamna/web-client/hooks/use-shape.ts";
import { useFamilyWords } from "./families/actions.ts";
import { HomeView, type RecordPaymentInput } from "./home/view.tsx";
import { useSetupHelp } from "./setup/frame.tsx";
import { setupPath, stepCopy, stepNumber } from "./setup/steps.ts";
import { TodayCard } from "./home/today-card.tsx";
import { OpenSpotsCard } from "./home/open-spots-card.tsx";
import { RenewalsCard } from "./home/renewals-card.tsx";
import { useStaffPhotos } from "./staff-photos.ts";
import { TodoCard } from "./todo/todo-card.tsx";
import { SuggestionsCard, type SuggestionsReadOnly } from "./assistant/suggestions-card.tsx";

/**
 * Owner Home (2026-10-04, replaces web-app-v2 slice B's Home).
 *
 * Five numbers the owner reads every morning, each with the people behind it (`home/view.tsx`):
 * the participants, who came last week, the seats booked this week, who has not been seen for two
 * weeks, and who is running out of sessions with what is owed. Above them, under the greeting, the
 * Today card (`home/today-card.tsx`, 2026-10-19): today's sessions, with attendance one click away.
 * Where clients book for themselves, the open spots card (`home/open-spots-card.tsx`, H2) and the
 * Renewals card (`home/renewals-card.tsx`, H3, in place of the Running out tile) follow.
 * Then the To do list (`todo/todo-card.tsx`, 2026-10-16): every job that waits for the owner, each
 * one done in place. Then, with AI Assistant on, its suggestions (`assistant/suggestions-card.tsx`,
 * 2026-10-21). Then the setup card while setup is open.
 *
 * `suggestionsReadOnly`: Control Center, acting as the business, shows the suggestions but never
 * opens one (see `SuggestionsCard`).
 */
export function OwnerHomeScreen({ suggestionsReadOnly }: { suggestionsReadOnly?: SuggestionsReadOnly } = {}) {
  const { business, isLoading, error } = useActiveBusiness();
  const me = useMe();
  const businessId = business?.businessId ?? null;
  const firstName = me.data?.user.fullName?.split(/\s+/)[0] ?? null;

  if (isLoading && !business) return <SkeletonList />;
  if (error && !business) return <ErrorState error={error} />;
  if (!businessId || !business) {
    return (
      <div>
        <PageHeader title="Home" />
        <EmptyState
          icon={<LockIcon size={30} />}
          title="No business to manage"
          message="This account does not run a business yet."
          action={
            <Link to="/start/business" className="font-semibold text-accent-strong underline">
              Create a business
            </Link>
          }
        />
      </div>
    );
  }
  // The front desk (T1) gets the day and its To do list, not the owner's numbers (the API refuses
  // them), unless the owner turned on "Home numbers" in Settings, Front desk (D65).
  if (isFrontDesk(business.staffRole) && !frontDeskMay(business.frontDesk, "homeNumbers")) {
    return (
      <FrontDeskHome
        business={business}
        businessId={businessId}
        timezone={business.timezone}
        needsName={Boolean(me.data && !me.data.user.fullName)}
      />
    );
  }
  return (
    <div>
      <HomeBody
        business={business}
        businessId={businessId}
        timezone={business.timezone}
        businessName={business.name}
        brandColor={business.logo?.accent ?? null}
        sport={business.sport}
        firstName={firstName}
        needsName={Boolean(me.data && !me.data.user.fullName)}
        assistant={business.aiAssistant ? business : null}
        suggestionsReadOnly={suggestionsReadOnly}
      />
    </div>
  );
}

/** Home for the front desk (T1): today's sessions and the jobs it may do. Exported for Storybook. */
export function FrontDeskHome({
  business,
  businessId,
  timezone,
  needsName,
}: {
  business: MyBusiness;
  businessId: string;
  timezone: string;
  needsName: boolean;
}) {
  const words = useFamilyWords();
  // Built once per mount, like the owner Home, so the days do not move under the person's hand.
  const [now] = useState(() => new Date());
  return (
    <div className="flex flex-col gap-4">
      <PageHeader title="Home" />
      {needsName ? <NameCard members={words.members} /> : null}
      <TodayCard key={`today-${businessId}`} business={business} />
      <TodoCard key={businessId} businessId={businessId} timezone={timezone} words={words} now={now} />
    </div>
  );
}

function HomeBody({
  business,
  businessId,
  timezone,
  businessName,
  brandColor,
  sport,
  firstName,
  needsName,
  assistant,
  suggestionsReadOnly,
}: {
  business: MyBusiness;
  businessId: string;
  timezone: string;
  businessName: string;
  /** The main colour of the live logo, or null (decision D7). */
  brandColor: string | null;
  /** The business sport: the drawing on the greeting band. */
  sport: Sport;
  firstName: string | null;
  /** The account has no name yet: the card that asks for it comes first. */
  needsName: boolean;
  /** The business when AI Assistant is on: its suggestions show under To do. */
  assistant: MyBusiness | null;
  suggestionsReadOnly?: SuggestionsReadOnly;
}) {
  const home = useOwnerHome(businessId);
  // Setup is the owner's: the front desk (T1) gets no setup card, and the API refuses it the steps.
  const progress = useSetupProgress(isFrontDesk(business.staffRole) ? null : businessId);
  const send = useSendNudges(businessId);
  // The messages of Home: the owner and admins, and the front desk with "Message many clients" on.
  const onSend = deskMay(business.staffRole, "sendNudges", business.frontDesk)
    ? (items: Array<{ participantId: string; kind: OwnerNudgeKind }>) => send.mutateAsync({ items })
    : undefined;
  const recordPayment = useRecordPayment(businessId);
  const sellPackage = useSellPackage(businessId);
  const staffPhotoOf = useStaffPhotos(businessId);
  // Built once per mount, so the greeting and the days do not move under the owner's hand.
  const [now] = useState(() => new Date());

  const words = useFamilyWords();

  async function onRecordPayment(input: RecordPaymentInput) {
    const common = { participantId: input.participantId, method: input.method, amountFils: input.amountFils, paidOn: input.paidOn, idempotencyKey: newIdempotencyKey() };
    if (input.purchaseId) await recordPayment.mutateAsync({ ...common, purchaseId: input.purchaseId });
    else await sellPackage.mutateAsync({ ...common, productId: input.productId! });
  }

  const setup =
    progress.data && progress.data.done < progress.data.total ? <SetupCard progress={progress.data} /> : null;

  if (home.isLoading) return <SkeletonList rows={4} />;
  if (home.error || !home.data) return <ErrorState error={home.error} onRetry={() => void home.refetch()} />;

  return (
    <HomeView
      home={home.data}
      words={words}
      timezone={timezone}
      businessName={businessName}
      brandColor={brandColor}
      sport={sport}
      firstName={firstName}
      now={now}
      onSend={onSend}
      onRecordPayment={onRecordPayment}
      staffPhotoOf={staffPhotoOf}
      top={
        <>
          <TodayCard key={`today-${businessId}`} business={business} />
          <OpenSpotsCard key={`open-spots-${businessId}`} business={business} />
          <RenewalsCard
            key={`renewals-${businessId}`}
            home={home.data}
            words={words}
            timezone={timezone}
            businessName={businessName}
            now={now}
            onSend={onSend}
            onRecordPayment={onRecordPayment}
            staffPhotoOf={staffPhotoOf}
          />
          <TodoCard key={businessId} businessId={businessId} timezone={timezone} words={words} now={now} />
          {assistant ? <SuggestionsCard key={`suggestions-${businessId}`} business={assistant} readOnly={suggestionsReadOnly} /> : null}
          {setup}
        </>
      }
    >
      {needsName ? <NameCard members={words.members} /> : null}
    </HomeView>
  );
}

/**
 * Asks an owner with no name for one (2026-10-09). An owner who signed up with a number had none,
 * and the landing used to send them to the member profile to add it. Families and the team see
 * this name on messages and bookings, in the business's own word for members.
 */
function NameCard({ members }: { members: string }) {
  const update = useUpdateMe();
  const [name, setName] = useState("");
  const [problem, setProblem] = useState<string | null>(null);

  function save() {
    const fullName = name.trim();
    if (fullName.length < 2) return setProblem("Write your full name.");
    setProblem(null);
    update.mutate({ fullName }, { onSuccess: () => showToast(`Thank you, ${fullName.split(/\s+/)[0]}.`, { tone: "success" }) });
  }

  return (
    <section aria-labelledby="name-card-title" className="rounded-card border border-line bg-paper p-5 shadow-card lg:p-6">
      <form
        className="flex flex-col gap-4 lg:flex-row lg:items-end"
        onSubmit={(event) => {
          event.preventDefault();
          save();
        }}
      >
        <div className="min-w-0 flex-1">
          <h2 id="name-card-title" className="text-[19px]">
            Add your name
          </h2>
          <p className="mt-1 text-[14px] text-ink-2">Your team and the {members} see it on messages and bookings.</p>
          <div className="mt-3 max-w-[420px]">
            <Field
              label="Your full name"
              value={name}
              maxLength={120}
              autoComplete="name"
              error={problem ?? undefined}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
        </div>
        <Button type="submit" loading={update.isPending}>
          Save
        </Button>
      </form>
      {update.error ? <InlineError error={update.error} /> : null}
    </section>
  );
}

/**
 * The setup checklist on Home, while setup is open: the steps the API lists for this business (seven
 * where clients book for themselves, eight for families), the next one first. Exported for Storybook.
 */
export function SetupCard({ progress }: { progress: NonNullable<ReturnType<typeof useSetupProgress>["data"]> }) {
  const { label } = useLabels();
  const { bookingFor } = useShape().shape;
  const help = useSetupHelp();
  const navigate = useNavigate();
  const next = progress.steps.find((step) => step.key === progress.nextStep) ?? null;

  if (progress.done >= progress.total || !next) {
    return (
      <section className="flex items-center gap-3 rounded-card border border-confirmed/25 bg-confirmed-surface px-5 py-4 text-confirmed">
        <CheckIcon size={20} />
        <p className="text-[15px] font-semibold">
          Setup is complete. Everything you need each day is in the menu.
        </p>
      </section>
    );
  }

  const copy = stepCopy(next.key, label, bookingFor);
  const percent = Math.round((progress.done / progress.total) * 100);

  return (
    <section
      aria-labelledby="setup-card-title"
      className="rounded-card border border-line bg-paper p-5 shadow-card lg:p-6"
    >
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0 flex-1">
          <h2 id="setup-card-title" className="text-[19px]">
            Setup, {progress.done} of {progress.total} done
          </h2>
          <progress
            aria-label="Setup progress"
            max={progress.total}
            value={progress.done}
            className="mt-3 block h-2 w-full max-w-[520px] appearance-none overflow-hidden rounded-pill bg-sunken [&::-moz-progress-bar]:bg-accent [&::-webkit-progress-bar]:bg-sunken [&::-webkit-progress-value]:rounded-pill [&::-webkit-progress-value]:bg-accent"
          >
            {percent}%
          </progress>
          <p className="mt-4 text-[15px] font-semibold text-ink">
            Next up: {copy.title}{" "}
            <span className="font-normal text-ink-3">(step {stepNumber(next.key, bookingFor)})</span>
          </p>
          <p className="mt-1 text-[14px] text-ink-2">{copy.description}</p>
        </div>
        <div className="shrink-0">
          {/* Opening a setup page writes nothing, so it stays on when the plan is read-only (review). */}
          <Button readOnlySafe onClick={() => void navigate({ to: setupPath(next.key) })}>
            Start
          </Button>
        </div>
      </div>
      <ol className="mt-5 grid gap-1.5 border-t border-line pt-4 sm:grid-cols-2 lg:grid-cols-4">
        {progress.steps.map((step) => (
          <li key={step.key}>
            <Link
              to={setupPath(step.key)}
              className={[
                "flex min-h-9 items-center gap-2 rounded-lg px-2 text-[13px] hover:bg-surface",
                step.done
                  ? "text-ink-3"
                  : step.key === next.key
                    ? "font-semibold text-accent-strong"
                    : "text-ink-2",
              ].join(" ")}
            >
              <span
                aria-hidden="true"
                className={[
                  "flex size-5 shrink-0 items-center justify-center rounded-full text-[11px] font-bold",
                  step.done ? "bg-confirmed text-inverse" : "border border-line-strong",
                ].join(" ")}
              >
                {step.done ? <CheckIcon size={12} /> : stepNumber(step.key, bookingFor)}
              </span>
              <span className={step.done ? "line-through" : ""}>{stepCopy(step.key, label, bookingFor).title}</span>
              {step.done ? <span className="sr-only">(done)</span> : null}
            </Link>
          </li>
        ))}
      </ol>
      {help ? (
        // Decision D7: a way to a person from the checklist, while setup is open.
        <p className="mt-4 text-[14px] text-ink-2">
          Want a hand with setup?{" "}
          <a
            href={help.callUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => help.onCallOpened?.("home")}
            className="font-semibold text-accent-strong"
          >
            Book a call with us
          </a>
          .
        </p>
      ) : null}
    </section>
  );
}
